import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createTPRMTemplateApi,
  getTPRMCustomerAdminOptionsApi,
  getTPRMTemplateApi,
  getTPRMTemplatesApi,
  updateTPRMTemplateApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import { useAuth } from "../../../context/AuthContext";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";
import QuestionnaireEditorModal from "./QuestionnaireEditorModal";
import { StatusBadge } from "./TPRMResourceTable";
import {
  displayTPRMValue,
  formatTPRMDate,
  getTPRMErrorMessage,
  normalizeTPRMPage,
} from "./tprmPageUtils";

const PAGE_SIZE = 20;
const PLATFORM_ADMIN_ROLES = new Set([
  "admin",
  "platform_admin",
  "superadmin",
]);

const QuestionnaireManagementPage = () => {
  const { user, isPlatformAdmin } = useAuth();
  const platformAdmin =
    Boolean(isPlatformAdmin) ||
    PLATFORM_ADMIN_ROLES.has(String(user?.role || "").toLowerCase());
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [actionId, setActionId] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [customerAdmins, setCustomerAdmins] = useState([]);
  const [customerAdminsLoading, setCustomerAdminsLoading] = useState(true);
  const [customerAdminsError, setCustomerAdminsError] = useState("");
  const [customerAdminsReloadKey, setCustomerAdminsReloadKey] = useState(0);
  const dialogTriggerRef = useRef(null);
  const filterValues = useMemo(
    () => Object.fromEntries(columnFilters.map(({ id, value }) => [id, value])),
    [columnFilters],
  );

  useEffect(() => {
    if (!platformAdmin) {
      setCustomerAdminsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const loadCustomerAdmins = async () => {
      setCustomerAdminsLoading(true);
      setCustomerAdminsError("");
      try {
        const response = await getTPRMCustomerAdminOptionsApi();
        const options = Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
            ? response.results
            : [];
        if (!cancelled) setCustomerAdmins(options);
      } catch (requestError) {
        if (!cancelled) {
          setCustomerAdminsError(
            getTPRMErrorMessage(
              requestError,
              "Unable to load customer administrators.",
            ),
          );
        }
      } finally {
        if (!cancelled) setCustomerAdminsLoading(false);
      }
    };
    loadCustomerAdmins();
    return () => {
      cancelled = true;
    };
  }, [platformAdmin, customerAdminsReloadKey]);

  const retryCustomerAdmins = () =>
    setCustomerAdminsReloadKey((current) => current + 1);

  useEffect(() => {
    if (!platformAdmin) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const loadTemplates = async () => {
      setLoading(true);
      setError("");
      try {
        const activeFilter = columnFilters.find(
          (filter) => filter.id === "is_active",
        );
        const response = await getTPRMTemplatesApi({
          ...filterValues,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          is_active:
            activeFilter?.value === undefined
              ? undefined
              : String(activeFilter.value),
        });
        const page = normalizeTPRMPage(response);
        if (!cancelled) {
          setRows(page.results);
          setRowCount(page.count);
          setHasLoaded(true);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            getTPRMErrorMessage(
              requestError,
              "Unable to load assessment questionnaires.",
            ),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [
    columnFilters,
    pagination.pageIndex,
    pagination.pageSize,
    platformAdmin,
    reloadKey,
  ]);

  const retry = () => setReloadKey((current) => current + 1);

  const openView = async (template, trigger) => {
    dialogTriggerRef.current = trigger;
    setViewLoading(true);
    setViewError("");
    try {
      const details = await getTPRMTemplateApi(template.id);
      setModal({ mode: "view", template: details });
    } catch (requestError) {
      setViewError(
        getTPRMErrorMessage(
          requestError,
          "Unable to load questionnaire details.",
        ),
      );
    } finally {
      setViewLoading(false);
    }
  };

  const openEditor = (mode, template = null, trigger = null) => {
    dialogTriggerRef.current = trigger;
    setSaveError("");
    setFeedback(null);
    setModal({ mode, template });
  };

  const saveTemplate = async (payload) => {
    setSaving(true);
    setSaveError("");
    try {
      if (modal.mode === "create") {
        await createTPRMTemplateApi(payload);
        setFeedback({
          type: "success",
          message: "Questionnaire created successfully.",
        });
        setPagination((current) => ({ ...current, pageIndex: 0 }));
      } else {
        await updateTPRMTemplateApi(modal.template.id, payload);
        setFeedback({
          type: "success",
          message:
            "Questionnaire updated. Existing assessment snapshots remain unchanged.",
        });
      }
      setModal(null);
      retry();
    } catch (requestError) {
      setSaveError(
        getTPRMErrorMessage(
          requestError,
          "Unable to save the questionnaire.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.is_active;
    setActionId(String(template.id));
    setFeedback(null);
    try {
      await updateTPRMTemplateApi(template.id, { is_active: nextActive });
      setFeedback({
        type: "success",
        message: `Questionnaire ${nextActive ? "activated" : "deactivated"}. ${
          nextActive
            ? "It is available for new assessments."
            : "Historical assessment snapshots remain available."
        }`,
      });
      retry();
    } catch (requestError) {
      setFeedback({
        type: "error",
        message: getTPRMErrorMessage(
          requestError,
          "Unable to update questionnaire activation.",
        ),
      });
    } finally {
      setActionId("");
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "search",
        accessorFn: (template) => template.name,
        header: "Questionnaire",
        size: 250,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
        meta: {
          filter: {
            variant: "text",
            label: "Questionnaire name",
            placeholder: "Search questionnaire",
          },
        },
      },
      {
        accessorKey: "framework",
        header: "Framework",
        size: 150,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
        meta: {
          filter: {
            variant: "text",
            label: "Framework",
            placeholder: "Filter framework",
          },
        },
      },
      {
        accessorKey: "version",
        header: "Version",
        size: 100,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "template_version",
        header: "Questionnaire revision",
        size: 155,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "is_active",
        header: "Status",
        size: 120,
        meta: {
          filter: {
            variant: "boolean",
            label: "Status",
            trueLabel: "Active",
            falseLabel: "Inactive",
          },
        },
        cell: ({ getValue }) => (
          <StatusBadge value={getValue() ? "Active" : "Inactive"} />
        ),
        meta: {
          filter: {
            variant: "select",
            label: "Status",
            options: [
              { value: "true", label: "Active" },
              { value: "false", label: "Inactive" },
            ],
          },
        },
      },
      {
        accessorKey: "risk_domains",
        header: "Risk domains",
        size: 220,
        cell: ({ getValue }) =>
          Array.isArray(getValue()) && getValue().length
            ? getValue().join(", ")
            : "—",
      },
      {
        accessorKey: "updated_at",
        header: "Updated",
        size: 145,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        size: 145,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
      {
        accessorKey: "tenant_id",
        header: "Customer tenant ID",
        size: 210,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        size: 250,
        cell: ({ row }) => {
          const template = row.original;
          const canManage = Boolean(template.tenant_id);
          const working = actionId === String(template.id);
          return (
            <div className="flex flex-wrap gap-2">
              <button
                aria-label={`View questionnaire ${template.name}`}
                className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                disabled={viewLoading || working}
                onClick={(event) =>
                  openView(template, event.currentTarget)
                }
                type="button"
              >
                {viewLoading ? "Loading..." : "View"}
              </button>
              {canManage && (
                <>
                  <button
                    aria-label={`Edit questionnaire ${template.name}`}
                    className="whitespace-nowrap rounded-lg border border-blue-200 bg-blue-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-blue-900 dark:focus:ring-offset-slate-900"
                    disabled={working}
                    onClick={(event) =>
                      openEditor("edit", template, event.currentTarget)
                    }
                    title="Edit"
                    type="button"
                  >
                    Edit
                  </button>
                  <button
                    aria-label={`${template.is_active ? "Deactivate" : "Activate"} questionnaire ${template.name}`}
                    className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                    disabled={working}
                    onClick={() => toggleActive(template)}
                    type="button"
                  >
                    {working
                      ? "Updating..."
                      : template.is_active
                        ? "Deactivate"
                        : "Activate"}
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [actionId, viewLoading],
  );

  if (!platformAdmin) {
    return (
      <section
        className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
        role="alert"
      >
        Questionnaire management is available to Platform Admin users only.
      </section>
    );
  }

  if (loading && !hasLoaded) {
    return (
      <PageLoader
        minH="min-h-[40vh]"
        title="Loading assessment questionnaires..."
      />
    );
  }

  if (error) {
    return (
      <section
        className="rounded-xl border border-rose-200 bg-white p-6 dark:border-rose-900 dark:bg-slate-900"
        role="alert"
      >
        <h2 className="font-semibold text-slate-900 dark:text-white">
          Questionnaires could not be loaded
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {error}
        </p>
        <button
          className="mt-4 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200"
          onClick={retry}
          type="button"
        >
          Try again
        </button>
      </section>
    );
  }

  return (
    <>
      <section
        aria-labelledby="questionnaire-management-heading"
        className="space-y-4"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2
              className="text-lg font-semibold text-slate-900 dark:text-white"
              id="questionnaire-management-heading"
            >
              Questionnaire Management
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Create and maintain tenant-owned assessment templates. Changes
              apply to new assessments; existing assessment snapshots are
              preserved.
            </p>
          </div>
          <button
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
            onClick={(event) =>
              openEditor("create", null, event.currentTarget)
            }
            type="button"
          >
            Create questionnaire
          </button>
        </div>

        {viewError && (
          <p
            className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
            role="alert"
          >
            {viewError}
          </p>
        )}
        {feedback && (
          <p
            className={`rounded-lg border p-3 text-sm ${
              feedback.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                : "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
            }`}
            role={feedback.type === "success" ? "status" : "alert"}
          >
            {feedback.message}
          </p>
        )}
        <DataTable
          className="min-w-0 [&_.overflow-x-auto]:dark:border-slate-700 [&_.overflow-x-auto]:dark:bg-slate-900 [&_thead]:dark:bg-slate-800 [&_thead]:dark:text-slate-300 [&_th]:dark:border-slate-700 [&_tbody]:dark:divide-slate-800 [&_tbody_tr]:dark:hover:bg-slate-800 [&_tbody_td]:dark:text-slate-300 [&_td]:dark:border-slate-800 [&_tfoot]:dark:bg-slate-900 [&_tfoot]:dark:text-slate-300"
          columns={columns}
          data={rows}
          emptyMessage="No assessment questionnaires found."
          columnFilters={columnFilters}
          enableColumnFilters
          enableColumnVisibility={false}
          enableGlobalFilter={false}
          enableSorting={false}
          getRowId={(template) => String(template.id)}
          loading={loading}
          manualPagination
          manualFiltering
          columnFilters={columnFilters}
          onColumnFiltersChange={(updater) => {
            const next = typeof updater === "function" ? updater(columnFilters) : updater;
            setColumnFilters(next);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          pageCount={Math.ceil(rowCount / pagination.pageSize)}
          pagination={pagination}
          rowCount={rowCount}
          manualFiltering
          onColumnFiltersChange={(updater) => {
            setColumnFilters(updater);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          onPaginationChange={(updater) =>
            setPagination((current) =>
              typeof updater === "function" ? updater(current) : updater,
            )
          }
        />
      </section>

      {modal && (
        <QuestionnaireEditorModal
          customerAdmins={customerAdmins}
          customerAdminsError={customerAdminsError}
          customerAdminsLoading={customerAdminsLoading}
          error={saveError}
          isCreate={modal.mode === "create"}
          onClose={() => setModal(null)}
          onRetryCustomerAdmins={retryCustomerAdmins}
          onSave={saveTemplate}
          readOnly={modal.mode === "view"}
          returnFocusRef={dialogTriggerRef}
          saving={saving}
          template={modal.template}
        />
      )}
    </>
  );
};

export default QuestionnaireManagementPage;
